/**
 * PromptDialog — ACM-styled textový dialog (nahrazuje native window.prompt()).
 *
 * Nativní prompt() je systémový chrome mimo motiv. Stejný důvod jako ConfirmDialog.
 *
 * Použití:
 *   <PromptDialog
 *     open={addOpen}
 *     title="Nová záložka"
 *     label="Název"
 *     placeholder="např. Provoz"
 *     confirmLabel="Vytvořit"
 *     onConfirm={(value) => { add(value); setAddOpen(false); }}
 *     onCancel={() => setAddOpen(false)}
 *   />
 */
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { Label } from './Label';

interface PromptDialogProps {
  open: boolean;
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Povolit odeslání prázdného řetězce (default false). */
  allowEmpty?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  open,
  title,
  description,
  label,
  placeholder,
  defaultValue = '',
  confirmLabel,
  cancelLabel,
  allowEmpty = false,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const { t } = useTranslation();
  const ok = confirmLabel ?? t('ui.confirm');
  const cancel = cancelLabel ?? t('ui.cancel');
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setValue(defaultValue);
    // Po mountu modalu — focus do pole (next tick kvůli portálu)
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 30);
    return () => window.clearTimeout(t);
  }, [open, defaultValue]);

  const trimmed = value.trim();
  const canSubmit = allowEmpty || trimmed.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onConfirm(allowEmpty ? value : trimmed);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size='sm'
      footer={
        <>
          <Button variant='ghost' size='sm' onClick={onCancel}>
            {cancel}
          </Button>
          <Button variant='primary' size='sm' onClick={submit} disabled={!canSubmit}>
            {ok}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className='space-y-3'>
        {description && <p className='text-sm text-gray-400'>{description}</p>}
        {label && <Label htmlFor='acm-prompt-input'>{label}</Label>}
        <Input
          id='acm-prompt-input'
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete='off'
        />
      </form>
    </Modal>
  );
}
